import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { ErrorPanel } from "../../shell/ErrorPanel";
import { HierarchyApi } from "../../admin/hierarchy/api";
import { VotersApi, type VoterCount } from "./api";

/**
 * Headline summary for the Voters screen — total registered, total ours,
 * gap, top provinces, and a per-province table sorted by registered. Pulls
 * district-level voterCounts and joins them to district records to derive
 * the `province` for grouping.
 */
export function VoterSummary({ period }: { period: string }) {
  const counts = useQuery({
    queryKey: ["voter-counts", "district", period],
    queryFn: () => VotersApi.list({ scopeLevel: "district", period }),
  });
  const districts = useQuery({ queryKey: ["districts"], queryFn: HierarchyApi.listDistricts });

  const byProvince = useMemo(() => {
    const m = new Map<string, string>();
    for (const d of districts.data?.items ?? []) {
      const prov = (d as unknown as { province?: string }).province;
      if (prov) m.set(d.id, prov);
    }
    const agg = new Map<string, { province: string; registered: number; ours: number; districts: number }>();
    for (const c of counts.data?.items ?? []) {
      const prov = m.get(c.scopeId);
      if (!prov) continue;
      const cur = agg.get(prov) ?? { province: prov, registered: 0, ours: 0, districts: 0 };
      cur.registered += c.registered;
      cur.ours += c.ours;
      cur.districts += 1;
      agg.set(prov, cur);
    }
    return Array.from(agg.values()).sort((a, b) => b.registered - a.registered);
  }, [counts.data, districts.data]);

  const totals = useMemo(() => {
    const t = byProvince.reduce(
      (acc, p) => ({ registered: acc.registered + p.registered, ours: acc.ours + p.ours }),
      { registered: 0, ours: 0 },
    );
    return {
      registered: t.registered,
      ours: t.ours,
      gap: t.registered - t.ours,
      pct: t.registered > 0 ? (t.ours / t.registered) * 100 : 0,
    };
  }, [byProvince]);

  return (
    <div>
      {counts.error && <ErrorPanel err={counts.error} title="Couldn't load voter counts" />}
      {districts.error && <ErrorPanel err={districts.error} title="Couldn't load districts" />}

      <div className="row-3" style={{ gap: 12, marginBottom: 16 }}>
        <Stat label="Total registered" value={totals.registered.toLocaleString()} />
        <Stat
          label="Ours (estimated)"
          value={totals.ours.toLocaleString()}
          sub={totals.registered > 0 ? `${totals.pct.toFixed(1)}%` : undefined}
        />
        <Stat label="Gap to close" value={totals.gap.toLocaleString()} />
      </div>

      <div className="card" style={{ padding: 16 }}>
        <div className="card-title">By province ({period})</div>
        {byProvince.length === 0 && !counts.isLoading && (
          <p className="muted" style={{ fontSize: 13 }}>
            No district-level voter counts for {period} yet. Run the Zambia voter-stats
            seed or capture data via <strong>+ New count</strong>.
          </p>
        )}
        {byProvince.length > 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 8 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted)" }}>
                <th style={{ padding: "6px 8px" }}>Province</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Registered</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Ours</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>%</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Share of national</th>
                <th style={{ padding: "6px 8px", textAlign: "right" }}>Districts</th>
              </tr>
            </thead>
            <tbody>
              {byProvince.map((p) => {
                const oursPct = p.registered > 0 ? (p.ours / p.registered) * 100 : 0;
                const share = totals.registered > 0 ? (p.registered / totals.registered) * 100 : 0;
                return (
                  <tr key={p.province} style={{ borderTop: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 8px", fontWeight: 500 }}>{p.province}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{p.registered.toLocaleString()}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{p.ours.toLocaleString()}</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{oursPct.toFixed(1)}%</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{share.toFixed(1)}%</td>
                    <td style={{ padding: "6px 8px", textAlign: "right" }}>{p.districts}</td>
                  </tr>
                );
              })}
              <tr style={{ borderTop: "2px solid var(--border)", fontWeight: 600 }}>
                <td style={{ padding: "6px 8px" }}>Total</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>{totals.registered.toLocaleString()}</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>{totals.ours.toLocaleString()}</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>{totals.pct.toFixed(1)}%</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>100%</td>
                <td style={{ padding: "6px 8px", textAlign: "right" }}>
                  {byProvince.reduce((s, p) => s + p.districts, 0)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="card" style={{ padding: 12 }}>
      <div style={{ fontSize: 11, color: "var(--muted)", textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600, marginTop: 4 }}>{value}</div>
      {sub && <div className="muted" style={{ fontSize: 12 }}>{sub}</div>}
    </div>
  );
}

// Re-exported so callers can grab it for a higher-level rollup.
export type ProvinceVoterAgg = {
  province: string;
  registered: number;
  ours: number;
  districts: number;
};
